import {
  Component, OnInit, AfterViewInit, OnDestroy,
  ElementRef, ViewChildren, QueryList, ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { OtppageService, ResendOtpData } from './otppage.service';
import { AuthorizationService } from '../authorization/authorization.service';
import { finalize } from 'rxjs/operators';
import { LoginService } from '../login/login.service';

@Component({
  selector: 'app-otppage',
  templateUrl: './otppage.component.html',
  styleUrls: ['./otppage.component.scss'],
})
export class OtppageComponent implements OnInit, AfterViewInit, OnDestroy {
  otpCode: string[] = new Array(6).fill('');
  timeLeft = 60;
  loading = false;
  resendLoading = false;
  otpError = '';                    // ← inline error message for the inputs

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  private interval: any;
  private userEmail = '';

  constructor(
    private toastr: ToastrService,
    private otppageService: OtppageService,
    private router: Router,
    private sharedauthservice: AuthorizationService,
    private loginService: LoginService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.startTimer();
    this.sharedauthservice.recoverAuthenticationData();
    this.initializeUserEmail();
  }

  ngAfterViewInit(): void {
    this.focusFirstInput();
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  // ─── Email Initialisation ─────────────────────────────────────────────────

  private initializeUserEmail(): void {
    this.userEmail = this.getUserEmailFromService();
    if (this.userEmail) {
      localStorage.setItem('usermail', this.userEmail);
      this.sharedauthservice.setUserEmail(this.userEmail);
    } else {
      this.showEmailNotFoundError();
    }
  }

  private showEmailNotFoundError(): void {
    this.toastr.error(
      'Email address not found. Please go back to registration and try again.',
      'Email Missing',
      { positionClass: 'toast-top-center', timeOut: 8000, closeButton: true, progressBar: true }
    );
    setTimeout(() => this.router.navigateByUrl('/getstarted'), 3000);
  }

  // ─── Timer ────────────────────────────────────────────────────────────────

  startTimer(): void {
    this.timeLeft = 60;
    this.clearTimer();
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        this.cdr.detectChanges();
      } else {
        this.clearTimer();
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  // ─── Focus Helpers ────────────────────────────────────────────────────────

  private focusFirstInput(): void {
    const inputs = this.otpInputs.toArray();
    if (inputs.length > 0) {
      inputs[0].nativeElement.focus();
    }
  }

  private focusInput(index: number): void {
    const inputs = this.otpInputs.toArray();
    if (inputs[index]) {
      inputs[index].nativeElement.focus();
    }
  }

  /** Select all text on focus so typing replaces existing digit instantly */
  onFocus(event: FocusEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    setTimeout(() => input.select(), 0);
  }

  /** On click, select text so user can immediately replace digit */
  onInputClick(event: MouseEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  // ─── Input Handling (mobile-safe) ─────────────────────────────────────────

  /**
   * INDUSTRY-STANDARD mobile OTP input handler.
   *
   * Key problems on Android/iOS with the naive approach:
   *  1. Android fires `input` before updating `value` in some browsers.
   *  2. `[(ngModel)]` + `maxlength="1"` can fight each other causing double chars.
   *  3. Composing events on virtual keyboards can produce multi-char strings.
   *
   * Solution:
   *  - Use one-way [value] binding (not [(ngModel)]) → we own the DOM value.
   *  - Always read input.value in the handler, strip non-digits, take only [0].
   *  - Manually sync back to the DOM via nativeElement.value.
   */
  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;

    // Strip all non-numeric characters
    const digits = raw.replace(/\D/g, '');

    if (digits.length === 0) {
      // User cleared the box
      this.otpCode[index] = '';
      input.value = '';
      this.otpError = '';
      this.cdr.detectChanges();
      return;
    }

    if (digits.length > 1) {
      // Multi-digit → treat as paste
      this.distributePaste(digits);
      return;
    }

    // Single digit — standard advance
    this.otpCode[index] = digits;
    input.value = digits;           // sync DOM immediately
    this.otpError = '';
    this.cdr.detectChanges();

    // Auto-advance to next field
    if (index < this.otpCode.length - 1) {
      this.focusInput(index + 1);
    }

    // Auto-submit when all filled
    if (!this.isOtpIncomplete) {
      setTimeout(() => this.verifyOtp(), 100);
    }
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const inputs = this.otpInputs.toArray();

    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.otpCode[index]) {
        // Clear current field first
        this.otpCode[index] = '';
        inputs[index].nativeElement.value = '';
        this.cdr.detectChanges();
      } else if (index > 0) {
        // Move to previous and clear it
        this.otpCode[index - 1] = '';
        inputs[index - 1].nativeElement.value = '';
        this.focusInput(index - 1);
        this.cdr.detectChanges();
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusInput(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < this.otpCode.length - 1) {
      event.preventDefault();
      this.focusInput(index + 1);
      return;
    }

    // Block non-numeric keys (allow control keys)
    if (
      !/^\d$/.test(event.key) &&
      !['Tab', 'Enter', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(event.key) &&
      !event.ctrlKey && !event.metaKey
    ) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '');
    if (digits) {
      this.distributePaste(digits);
    }
  }

  /** Distributes a string of digits across all OTP inputs */
  private distributePaste(digits: string): void {
    const inputs = this.otpInputs.toArray();
    const take = digits.substring(0, this.otpCode.length);

    take.split('').forEach((char, i) => {
      this.otpCode[i] = char;
      if (inputs[i]) {
        inputs[i].nativeElement.value = char;
      }
    });

    // Clear any remaining boxes
    for (let i = take.length; i < this.otpCode.length; i++) {
      this.otpCode[i] = '';
      if (inputs[i]) inputs[i].nativeElement.value = '';
    }

    // Focus the next empty box or the last one
    const focusIdx = Math.min(take.length, this.otpCode.length - 1);
    this.focusInput(focusIdx);
    this.otpError = '';
    this.cdr.detectChanges();

    if (!this.isOtpIncomplete) {
      setTimeout(() => this.verifyOtp(), 100);
    }
  }

  // ─── Computed ─────────────────────────────────────────────────────────────

  get isOtpIncomplete(): boolean {
    return this.otpCode.some((d) => !d);
  }

  // ─── Verify ───────────────────────────────────────────────────────────────

  verifyOtp(): void {
    this.otpError = '';
    const otpValue = this.otpCode.join('');

    if (otpValue.length !== 6) {
      this.otpError = 'Please enter all 6 digits.';
      this.cdr.detectChanges();
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.otppageService
      .submitSignup({ otpCode: otpValue })
      .pipe(finalize(() => { this.loading = false; this.cdr.detectChanges(); }))
      .subscribe({
        next: (response) => this.handleVerificationSuccess(response),
        error: (err) => this.handleVerificationError(err),
      });
  }

 private handleVerificationSuccess(response: any): void {
  this.toastr.success(response.message, 'Success', {
    positionClass: 'toast-top-center',
  });
  this.sharedauthservice.setOtpResponse(response);

  // CRITICAL FIX: Store token + user data to localStorage so the
  // authorization component can authenticate, just like login does.
  this.storeOtpSession(response);

  this.router.navigateByUrl('/auth-payment');
}

/**
 * authorization component finds a valid token after the OTP signup flow.
 */
private storeOtpSession(response: any): void {
  const token = response.token || response.accessToken || response.authToken;
  const email = response.email || this.userEmail;

  const sessionData: Record<string, string> = {
    token:          token || '',
    authToken:      token || '',
    tokenType:      response.tokenType || 'Bearer',
    userName:       response.name || '',
    userPhone:      response.phoneNumber || response.phone || '',
    usermail:       email || '',
    user_email:     email || '',
    loginTimestamp: new Date().toISOString(),
  };

  console.log('💾 [OTP] Storing session data to localStorage...');
  Object.entries(sessionData).forEach(([key, value]) => {
    if (value) {
      localStorage.setItem(key, value);
      sessionStorage.setItem(`backup_${key}`, value);
      console.log(`✅ [OTP] Stored ${key}`);
    } else {
      console.warn(`⚠️ [OTP] Skipping empty value for key "${key}" — check API response shape`);
    }
  });
}

  private handleVerificationError(err: any): void {
    const msg = this.extractErrorMessage(err);
    this.otpError = msg;
    // Shake the inputs on error
    this.otpCode = new Array(6).fill('');
    const inputs = this.otpInputs.toArray();
    inputs.forEach(i => i.nativeElement.value = '');
    this.focusFirstInput();
    this.cdr.detectChanges();

    this.toastr.error(msg, 'Verification Failed', {
      positionClass: 'toast-top-center', timeOut: 5000, closeButton: true, progressBar: true,
    });
  }

  private extractErrorMessage(err: any): string {
    return (
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      (typeof err.error === 'string' ? err.error : null) ||
      'Invalid OTP code. Please try again.'
    );
  }

  // ─── Resend ───────────────────────────────────────────────────────────────

  resendCode(): void {
    this.userEmail = this.getUserEmailFromService();
    if (!this.validateEmailForResend()) return;
    this.performResendRequest();
  }

  private validateEmailForResend(): boolean {
    if (!this.userEmail) {
      this.toastr.error('Email address not found.', 'Email Required', { positionClass: 'toast-top-center' });
      return false;
    }
    if (!this.isValidEmail(this.userEmail)) {
      this.toastr.error('Invalid email format detected.', 'Invalid Email', { positionClass: 'toast-top-center' });
      return false;
    }
    return true;
  }

  private performResendRequest(): void {
    this.resendLoading = true;
    this.cdr.detectChanges();
    const resendData: ResendOtpData = { email: this.userEmail };

    this.otppageService
      .resendOtp(resendData)
      .pipe(finalize(() => { this.resendLoading = false; this.cdr.detectChanges(); }))
      .subscribe({
        next: (response) => this.handleResendSuccess(response),
        error: (err) => this.handleResendError(err),
      });
  }

  private handleResendSuccess(response: any): void {
    this.otpCode = new Array(6).fill('');
    const inputs = this.otpInputs.toArray();
    inputs.forEach(i => i.nativeElement.value = '');
    this.otpError = '';
    this.startTimer();
    this.sharedauthservice.setUserEmail(this.userEmail);

    this.toastr.success(
      response.message || `OTP resent to ${this.maskEmail(this.userEmail)}`,
      'Success',
      { positionClass: 'toast-top-center', timeOut: 5000, closeButton: true, progressBar: true }
    );
    setTimeout(() => this.focusFirstInput(), 100);
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  goBack(): void {
    this.router.navigateByUrl('/signup');
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  trackByIndex(index: number): number {
    return index;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+$/.test(email);
  }

  private maskEmail(email: string): string {
    if (!email?.includes('@')) return email;
    const [local, domain] = email.split('@');
    const masked = local.charAt(0) + '*'.repeat(Math.max(local.length - 1, 2));
    return `${masked}@${domain}`;
  }

  // ─── Email Resolution (unchanged priority chain) ──────────────────────────

  private getUserEmailFromService(): string {
    const sources = [
      () => this.sharedauthservice.getOtpResponse()?.email,
      () => { try { return JSON.parse(localStorage.getItem('signupResponse') || '{}').email; } catch { return ''; } },
      () => { try { return JSON.parse(localStorage.getItem('tempUserData') || '{}').email; } catch { return ''; } },
      () => this.sharedauthservice.getUserEmail(),
      () => this.loginService?.getUserData()?.email,
      () => localStorage.getItem('usermail') || localStorage.getItem('user_email'),
      () => sessionStorage.getItem('backup_usermail') || sessionStorage.getItem('user_email'),
      () => { try { return JSON.parse(localStorage.getItem('loginResponse') || '{}').email; } catch { return ''; } },
    ];
    for (const fn of sources) {
      const email = fn();
      if (email) return email;
    }
    return '';
  }

  // ─── Resend Error Handling ────────────────────────────────────────────────

  private handleResendError(err: any): void {
    let msg = 'Failed to resend OTP. Please try again.';
    let title = 'Resend Failed';

    if (err.status === 404) { msg = err.error?.message || 'No account found with this email.'; title = 'Not Found'; }
    else if (err.status === 429) { msg = 'Too many requests. Please wait before retrying.'; title = 'Rate Limited'; }
    else if (err.status === 400) {
      if (err.error?.error === 'ALREADY_VERIFIED') {
        msg = 'Your email is already verified. Redirecting...'; title = 'Already Verified';
        setTimeout(() => this.router.navigateByUrl('/auth-payment'), 2000);
      } else {
        msg = err.error?.message || msg;
      }
    } else if (err.status === 500) {
      msg = err.error?.error === 'EMAIL_SEND_FAILED'
        ? 'Failed to send email. Check your connection.' : 'Server error. Try again later.';
      title = 'Server Error';
    }

    this.toastr.error(msg, title, {
      positionClass: 'toast-top-center', timeOut: 8000, closeButton: true, progressBar: true,
    });
  }
}